import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  MenuItem,
  Typography,
  Divider,
  Paper,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  Box,
} from "@mui/material";
import Select from "react-select";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { Add, Delete } from "@mui/icons-material";
import Footer from "../../Footer/Footer";
import { ICustomer } from "../../Interfaces/IInvoice";
import { getUsers } from "../../Services/userService";
import AddCustomerPopup from "../AddCustomerPopup";
import DeliveryDestination from "./DeliveryDestination";
import { getNewPoNumber, savePurchaseOrder } from "../../Services/PurchaseOrderService";
import { getItems } from "../../Services/inventoryService";
import { generatePurchaseOrderPDF, getCurrentFinancialYear, getStateCode, uomOptions } from "../../Services/CommonService";
import { useSnackbar } from "notistack";
import { getOrganizationDetails } from "../../Services/orgService";
import { Stack } from "@mui/material";
type POItem = {
  itemId: string;
  itemName: string;
  hsn: string;
  quantity: number;
  uom: string;
  rate: number;
  discount: number;
  taxPercent: number;
};

const defaultItem: POItem = {
  itemId: "",
  itemName: "",
  hsn: "",
  quantity: 0,
  uom: "PCS",
  rate: 0,
  discount: 0,
  taxPercent: 18,
};
type POData = {
  orgId: string;
  poNumber: string;
  serial: number;
  vendorId: string;
  vendorName: string;
  poDate: Dayjs | null;
  billingAddress: string;
  shippingAddress: string;
  paymentTerms: string;
  expectedDeliveryDate: Dayjs | null;
  items: POItem[];
  totalAmount?: number;
  totalTax?: number;
  discount?: number;
  grandTotal?: number;
  status?: "draft" | "sent";
  financialYear?: string;
};

export default function PurchaseOrderForm() {
  const [orgId, setOrgId] = useState<string>("");
  const { enqueueSnackbar } = useSnackbar();
  const [vendors, setVendors] = useState<ICustomer[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<ICustomer | null>(null);
  const [showNewVendorPopup, setShowNewVendorPopup] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [itemsKey, setItemsKey] = useState(0);
  const financialYear = getCurrentFinancialYear() ?? "";
  const [orgDetails, setOrgDetails] = useState<any>(null);
  const [sellerStateCode, setSellerStateCode] = useState<string>("");
  const [vendorStateCode, setVendorStateCode] = useState<string>("");
  useEffect(() => {
  const storedOrgId = localStorage.getItem("orgId") || "";
  setOrgId(storedOrgId);
}, []);
  const loadVendors = async () => {
    try {
      const vendorData = await getUsers(orgId, "vendor");
      const vendorList = vendorData?.map((vendor: any) => ({
        id: vendor.Id,
        name:
          vendor?.customerName ??
          (vendor?.companyName ||
            [vendor?.firstName, vendor?.lastName].filter(Boolean).join(" ")),
        email: vendor?.email || "",
        GSTIN: vendor?.GSTIN || "",
        phone: vendor?.phone || "",
        street: vendor?.address?.street || "",
        city: vendor?.address?.city || "",
        state: vendor?.address?.state || "",
        zipCode: vendor?.address?.zipCode || "",
        country: vendor?.address?.country || "",
        companyName: vendor?.companyName || "",
      }));
      setVendors(vendorList);
    } catch (error) {
      console.error("Error loading Vendors:", error);
    }
  };
  const fetchPoNumber = async () => {
    try {
      if (!orgId || !financialYear) return;
      const response = await getNewPoNumber(orgId, financialYear);
      if (response.success) {
        setPoData((prev) => ({ ...prev, poNumber: response.poNumber, serial: parseInt(response.poNumber.split("/").pop() || "0", 10) }));
      }
    } catch (err) {
      console.error("Failed to get PO number", err);
    }
  };
  const loadItems = async () => {
    try {
      const itemData = await getItems(orgId);
      const formattedItems = itemData.map((item: any) => ({
        value: item.itemId,
        label: `${item.name} ${item.desc || ""}`,
        price: item.sellingPrice,
        discount: 0,
        taxPercent: item.taxPercent || 0,
        hsn: item.hsn,
        quantity: 1,
        uom: item.uom || "PCS",
      }));
      setItems(formattedItems);
      setItemsKey((prev) => prev + 1);
    } catch (error) {
      console.error("Error loading items:", error);
    }
  };
  const resetForm = async () => {
    setSelectedVendor(null);
    setDeliveryType("warehouses");
    setSelectedDestination("");

    setPoData({
      poNumber: "",
      serial: 0,
      orgId: orgId,
      vendorId: "",
      vendorName: "",
      poDate: dayjs(),
      billingAddress: "",
      shippingAddress: "",
      paymentTerms: "",
      expectedDeliveryDate: dayjs(),
      items: [{ ...defaultItem }],
    });
    await fetchPoNumber();
  };
  const loadOrgDetails = async () => {
    try {

      const orgData = await getOrganizationDetails(orgId);
      setOrgDetails(orgData);
    }
    catch (error) {
      console.error("Error loading Org details:", error);
    }
  }


  useEffect(() => {
    if (!orgId) return;
    loadVendors();
    fetchPoNumber();
    loadItems();
    loadOrgDetails();
  }, [orgId]);

  const vendorOptions = [
    {
      value: "addNewVendor",
      label: "+ Add New Vendor",
    },
    ...vendors?.map((vendor) => ({
      value: vendor.id,
      label: vendor.name,
    })),
  ];
  const handleVendorChange = (selectedOption: { value: string; label: string }) => {
    if (!selectedOption) return;

    const value = selectedOption.value;
    if (value === "addNewVendor") {
      setShowNewVendorPopup(true);
    } else {
      const selected = vendors.find((vendor) => vendor.id === value);
      if (selected) {
        setSelectedVendor(selected);
      } else {
        console.error("Selected vendor not found");
      }
    }
  };
  const handleAddVendor = (newVendor: any) => {
    const newVendorObject = {
      id: newVendor.customerId,
      name:
        newVendor?.customerName ??
        (newVendor?.companyName ||
          [newVendor?.firstName, newVendor?.lastName]
            .filter(Boolean)
            .join(" ")),
      email: newVendor.email || "",
      phone: newVendor.phone || "",
      GSTIN: newVendor.GSTIN || "",
      street: newVendor.address?.street || "",
      city: newVendor.address?.city || "",
      state: newVendor.address?.state || "",
      zipCode: newVendor.address?.zipCode || "",
      country: newVendor.address?.country || "",
      companyName: newVendor?.companyName || "",
    };
    setVendors((prev) => [...prev, newVendorObject]);
    setSelectedVendor(newVendorObject);
  };

  const [poData, setPoData] = useState<POData>({
    poNumber: "",
    serial: 0,
    orgId: orgId,
    vendorId: "",
    vendorName: "",
    poDate: dayjs(),
    billingAddress: "",
    shippingAddress: "",
    paymentTerms: "",
    expectedDeliveryDate: dayjs(),
    items: [{ ...defaultItem }],
  });

  const [deliveryType, setDeliveryType] = useState<"warehouses" | "customer">(
    "warehouses"
  );
  const [selectedDestination, setSelectedDestination] = useState("");

  const handleItemChange = (
    index: number,
    field: keyof POItem,
    value: string | number
  ) => {
    const items = [...poData.items];

    if (field === "itemName") {
      // When item name changes, try to find matching item details
      const selectedItem = items.find((item) => item.itemName === value);
      if (selectedItem) {
        items[index] = {
          ...items[index],
          itemName: selectedItem.itemName,
          rate: selectedItem.rate,
          taxPercent: selectedItem.taxPercent,
        };
      } else {
        items[index][field] = value as string;
      }
    } else {
if (field === "quantity") {
  const num = value === "" ? 1 : Number(value);
  items[index].quantity = num < 1 ? 1 : num;
}
else if (field === "rate") {
  const num = value === "" ? 1 : Number(value);
  items[index].rate = num < 1 ? 1 : num;
}
else if (field === "discount") {
  const num = value === "" ? 0 : Number(value);
  items[index].discount = isNaN(num)
    ? 0
    : Math.min(Math.max(num, 0), 100);
}
   else if (field === "taxPercent") {
  items[index][field] =
    value === "" ? 0 : Number(value);
} else {
  items[index][field] = value as any;
}

    }
    setPoData({ ...poData, items });
};
 
 
  const addItem = () => {
    setPoData({ ...poData, items: [...poData.items, { ...defaultItem }] });
  };

  const removeItem = (index: number) => {
    if (poData.items.length <= 1) return;
    const items = [...poData.items];
    items.splice(index, 1);
    setPoData({ ...poData, items });
  };

  useEffect(() => {
    if (orgDetails?.address?.[0]?.state) {
      setSellerStateCode(getStateCode(orgDetails.address[0].state));
    }
  }, [orgDetails]);

  useEffect(() => {
    if (selectedVendor?.state) {
      setVendorStateCode(getStateCode(selectedVendor.state));
    }
  }, [selectedVendor]);
  const totalSummary = poData.items.reduce(
    (acc, item) => {
      const subtotal = item.quantity * item.rate;
      const discountAmt = (subtotal * item.discount) / 100;
      const taxable = subtotal - discountAmt;

      return {
        subTotal: acc.subTotal + subtotal,
        discountTotal: acc.discountTotal + discountAmt,
        taxableTotal: acc.taxableTotal + taxable,
      };
    },
    { subTotal: 0, discountTotal: 0, taxableTotal: 0 }
  );

  const isIntraState = sellerStateCode === vendorStateCode;

  let totalCGST: number = 0;
  let totalSGST: number = 0;
  let totalIGST: number = 0;

  poData.items.forEach(item => {
    const subtotal = item.quantity * item.rate;
    const discountAmt = (subtotal * item.discount) / 100;
    const taxable = subtotal - discountAmt;

    if (isIntraState) {
      // ➤ Intra-state → CGST + SGST
      const halfTaxRate = item.taxPercent / 2;

      totalCGST += (taxable * halfTaxRate) / 100;
      totalSGST += (taxable * halfTaxRate) / 100;
    } else {
      // ➤ Inter-state → IGST
      totalIGST += (taxable * item.taxPercent) / 100;
    }
  });
  const grandTotal =
    totalSummary.taxableTotal
    + totalCGST
    + totalSGST
    + totalIGST;

  const gstBreakup = {
    subTotal: totalSummary.subTotal || 0,
    discount: totalSummary.discountTotal || 0,
    taxable: totalSummary.taxableTotal || 0,
    cgst: totalCGST || 0,
    sgst: totalSGST || 0,
    igst: totalIGST || 0,
    grandTotal,
  };


  const handleSavePO = async (status: "draft" | "sent") => {
    try {
      if (!selectedVendor) {
        enqueueSnackbar("Please select a vendor before saving.", { variant: "error" });
        return;
      }
      const payload = {
        ...poData,
        orgId:orgId,
        vendorId: selectedVendor.id,
        vendorName: selectedVendor.name,
        poDate: poData.poDate?.toISOString() || null,
        expectedDeliveryDate: poData.expectedDeliveryDate?.toISOString() || null,
        totalAmount: totalSummary.subTotal,
        discount: totalSummary.discountTotal,
        totalTax: gstBreakup.cgst + gstBreakup.sgst + gstBreakup.igst,
        grandTotal: gstBreakup.grandTotal,
        status, // draft / sent
        financialYear: financialYear,
      };
      //generatePurchaseOrderPDF(orgDetails, poData, selectedVendor,gstBreakup,isIntraState);
      const res = await savePurchaseOrder(payload, poData.items);
      if (res.success) {
        enqueueSnackbar("Purchase Order saved successfully!", { variant: "success" });
        if (status === "sent") {
        generatePurchaseOrderPDF(orgDetails, poData, selectedVendor, gstBreakup, isIntraState);
      }
        await resetForm();
      } else {
        enqueueSnackbar("Failed to save PO: " + res.message, { variant: "error" });
      }
    } catch (error: any) {
      enqueueSnackbar(error.message || "Error saving purchase order", { variant: "error" });
    }
  };
  return (
    <>
      <div className="page-location">
        <span>Purchase &gt; Create Purchase Order</span>
      </div>
      <Paper elevation={3} sx={{ p: 3, m: 3 }}>
        <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="PO Number"
              value={poData.poNumber}
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="PO Date"
              format="DD/MM/YYYY"
              value={poData.poDate ? dayjs(poData.poDate) : null}
              onChange={(date) => setPoData({ ...poData, poDate: date })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label={
                <>
                  Select Vendor <span style={{ color: "red" }}>*</span>
                </>
              }
              value={selectedVendor?.id || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "addNewVendor") {
                  setShowNewVendorPopup(true);
                } else {
                  const selected = vendors.find((vendor) => vendor.id === value);
                  setSelectedVendor(selected || null);
                }
              }}
              InputLabelProps={{
                style: { color: 'black' },
              }}
            >
              <MenuItem value="addNewVendor" style={{ color: "#1976d2", fontWeight: 500 }}>
                + Add New Vendor
              </MenuItem>
              {vendors.map((vendor) => (
                <MenuItem key={vendor.id} value={vendor.id}>
                  {vendor.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Expected Delivery Date"
              format="DD/MM/YYYY"
              value={poData.expectedDeliveryDate ? dayjs(poData.expectedDeliveryDate) : null}
              onChange={(date) =>
                setPoData({ ...poData, expectedDeliveryDate: date })
              }
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Payment Terms"
              value={poData.paymentTerms}
              onChange={(e) =>
                setPoData({ ...poData, paymentTerms: e.target.value })
              }
            >
              <MenuItem value="Advance Payment">Advance Payment</MenuItem>
              <MenuItem value="Net 15">Net 15</MenuItem>
              <MenuItem value="Net 30">Net 30</MenuItem>
              <MenuItem value="Net 60">Net 60</MenuItem>
              <MenuItem value="Partial Payment">Partial Payment</MenuItem>
            </TextField>
          </Grid>
          {/* Delivery Type */}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Delivery Type"
              value={deliveryType}
              onChange={(e) =>
                setDeliveryType(e.target.value as "customer" | "warehouses")
              }
              fullWidth
            >
              <MenuItem value="customer" disabled>Customer</MenuItem>
              <MenuItem value="warehouses" defaultValue="warehouses">Warehouses</MenuItem>
            </TextField>
          </Grid>

          {/* Delivery Destination (from your component) */}
          <Grid item xs={12} sm={6}>
           {orgId && (
            <DeliveryDestination orgId={orgId} deliveryType={deliveryType} />
          )}
          </Grid>

          {/* {selectedVendor && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Billing Address"
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  value={poData.billingAddress}
                  onChange={(e) =>
                    setPoData({ ...poData, billingAddress: e.target.value })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Shipping Address"
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  value={poData.shippingAddress}
                  onChange={(e) =>
                    setPoData({ ...poData, shippingAddress: e.target.value })
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#f5f5f5",
                    },
                  }}
                />
              </Grid>
            </Grid>
          )} */}
          {/* ITEMS SECTION */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Items
            </Typography>
          </Grid>

          {poData.items.map((item, index) => (
            <Grid item xs={12} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Grid container spacing={2} alignItems="center">

                  {/* ITEM SELECT */}
                  <Grid item xs={12} md={4}>
                    <Select
                      options={items}
                      value={items.find((o) => o.value === item.itemId) || null}
                      onChange={(selectedOption) => {
                        if (!selectedOption) return;
                        const updated = [...poData.items];
                        updated[index] = {
                          ...updated[index],
                          itemId: selectedOption.value,
                          itemName: selectedOption.label,
                          rate: selectedOption.price,
                          hsn: selectedOption.hsn,
                          taxPercent: selectedOption.taxPercent,
                          uom: selectedOption.uom,
                        };
                        setPoData({ ...poData, items: updated });
                      }}
                      placeholder="Select Item"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: 56, // 👈 match MUI TextField height
                        }),
                      }}
                    />
                  </Grid>

                  {/* QUANTITY */}
                  <Grid item xs={6} sm={3} md={1.2}>
                    <TextField
                      label="Qty"
                      fullWidth
                      inputProps={{ min: 1 }}
                      type="number"
                      value={item.quantity === 0 ? "" : item.quantity}
                      onChange={(e) =>
                        handleItemChange(index, "quantity", e.target.value)
                      }
                   slotProps={{
                   htmlInput: {
                  min: 1,
                  },
                  input: {
                  onWheel: (e) => (e.target as HTMLInputElement).blur(),
                  },
                  }}
                    />
                  </Grid>

                  {/* UOM */}
                  <Grid item xs={6} sm={3} md={1.5}>
                    <TextField
                      select
                      label="UOM"
                      fullWidth
                      value={item.uom}
                      onChange={(e) =>
                        handleItemChange(index, "uom", e.target.value)
                      }
                    >
                      {uomOptions.map((u) => (
                        <MenuItem key={u.value} value={u.value}>
                          {u.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* RATE */}
                  <Grid item xs={6} sm={3} md={1.7}>
                    <TextField
  label="Rate"
  type="number"
  fullWidth
  value={item.rate === 0 ? "" : item.rate}
  onChange={(e) =>
    handleItemChange(index, "rate", e.target.value)
  }
  slotProps={{
    htmlInput: {
      min: 1,
      step: "any",
    },
    input: {
      onWheel: (e) => (e.target as HTMLInputElement).blur(),
    },
  }}
/>

                  </Grid>

                  {/* DISCOUNT */}
                  <Grid item xs={6} sm={3} md={1.5}>
<TextField
  label="Discount %"
  type="number"
  fullWidth
  value={item.discount}
  onChange={(e) =>
    handleItemChange(index, "discount", e.target.value)
  }
  onBlur={() => {
    let val = Number(item.discount);
    if (isNaN(val) || val < 0) val = 0;
    if (val > 100) val = 100;
    handleItemChange(index, "discount", val);
  }}
  slotProps={{
    htmlInput: {
      min: 0,
      max: 100,
      step: "any",
    },
    input: {
      onWheel: (e) => (e.target as HTMLInputElement).blur(),
    },
  }}
/>


                  </Grid>

                  {/* GST */}
                  <Grid item xs={6} sm={3} md={1.5}>
                    <TextField
                      select
                      label="GST %"
                      fullWidth
                      value={item.taxPercent}
                      onChange={(e) =>
                        handleItemChange(index, "taxPercent", e.target.value)
                      }
                    >
                      {[0, 5, 12, 18, 28].map((gst) => (
                        <MenuItem key={gst} value={gst}>
                          {gst}%
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* DELETE */}
                  <Grid    item  xs={6} sm={3} md={0.6} display="flex" justifyContent="center"  alignItems="center"  >
                    <IconButton
                      onClick={() => removeItem(index)}
                      disabled={poData.items.length <= 1}
                    >
                      <Delete
                        color={poData.items.length <= 1 ? "disabled" : "error"}
                      />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={addItem}
              sx={{ mt: 1 }}
            >
              Add Item
            </Button>
          </Grid>


          <Grid item xs={12}>
            <Divider sx={{ my: 3, borderColor: "divider" }} />

            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "8px",
                backgroundColor: "background.paper",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: "4px",
                    height: "16px",
                    bgcolor: "primary.main",
                    mr: 1.5,
                    borderRadius: "2px",
                  }}
                />
                ORDER SUMMARY
              </Typography>

              <Box sx={{ mb: 1 }}>
                <Grid container spacing={1} sx={{ mb: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2">Subtotal:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <Typography variant="body2" fontWeight={500}>
                      ₹{totalSummary.subTotal.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={1} sx={{ mb: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="error.main">
                      Discount:
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <Typography
                      variant="body2"
                      color="error.main"
                      fontWeight={500}
                    >
                      -₹{totalSummary.discountTotal.toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>

                {isIntraState ? (
                  <>
                    {/* CGST */}
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2">CGST:</Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: "right" }}>
                        <Typography variant="body2" fontWeight={500}>
                          ₹{gstBreakup.cgst.toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* SGST */}
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2">SGST:</Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: "right" }}>
                        <Typography variant="body2" fontWeight={500}>
                          ₹{gstBreakup.sgst.toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  /* IGST */
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2">IGST:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: "right" }}>
                      <Typography variant="body2" fontWeight={500}>
                        ₹{gstBreakup.igst.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                )}

              </Box>

              <Divider sx={{ my: 1, borderColor: "divider" }} />

              <Grid container spacing={1} sx={{ mt: 1.5 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Grand Total:
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ textAlign: "right" }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "primary.main",
                    }}
                  >
                    ₹{grandTotal.toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
  <Stack spacing={2} direction={{ xs: "column", sm: "row" }} justifyContent="center">
    <Button fullWidth variant="contained" onClick={() => handleSavePO("draft")}>
      Save as Draft
    </Button>
    <Button fullWidth variant="contained" color="success" onClick={() => handleSavePO("sent")}>
      Save and Send
    </Button>
  </Stack>
</Grid>

        </Grid>
        {showNewVendorPopup && (
          <AddCustomerPopup
            type="vendor"
            orgId={orgId}
            onSave={handleAddVendor}
            onClose={() => setShowNewVendorPopup(false)}
          />
        )}
      </Paper>
      <Box
        sx={{
          position: "relative",
          left: 0,
          bottom: 0,
          width: "100%",
          mt: 10,
        }}
      >
        <Footer />
      </Box>
    </>
  );
}
